import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import type { RootState, AppDispatch } from '../../../store';
import * as S from './styles';
import { Scaffold } from '../../../layouts';
import { useAuth } from '../../../hooks';
import { NavigationDashboard } from '../NavigationDashboard';
import { TabbedNavigation } from '../../stock-inspection/shared-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermission } from '../../../hooks/usePermission';
import { useDispatch, useSelector } from 'react-redux';
import { FetchEmbedZohoActions } from '../../../store/ducks/zohoDashboard';
import { Loading } from '../../../components/Shared/Loading';

const Indicators = () => {
  const { profile, userBelongsToAnyOf } = useAuth();
  const { hasPermissionTo } = usePermission();
  const { hash, pathname } = useLocation(); // Adicione pathname
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNavigator, setShowNavigator] = useState<boolean>(true);
  const dispatch: AppDispatch = useDispatch();

  const handleClick = useCallback(() => {
    if (ref.current) {
      if (window.document.fullscreenElement) {
        if (window.document.exitFullscreen) {
          window.document.exitFullscreen();
        }
      } else {
        ref.current.requestFullscreen();
      }
    }
  }, []);

  const handleKeyDown = useCallback(() => {
    setIsFullscreen((v) => !v);
    setShowNavigator((prev) => !prev);
  }, []);

  useEffect(() => {
    if (window.document) {
      window.document.addEventListener('fullscreenchange', handleKeyDown);
    }

    return () => {
      window.document.removeEventListener('fullscreenchange', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (
      ![
        '#rework',
        '#tracking',
        '#tracking-inbound-corteva',
        '#warehouse',
        '#inventory',
        '#rccp',
        '#interdict',
        '#metrics',
        '#projetouno',
        '#kpidetransporte',
        '#ocup-utrisha',
        '#temperature-control',
        '#multas',
      ].includes(hash) ||
      !hash
    ) {
      navigate('/dashboard#metrics');
    }
  }, [hash, navigate]);

  const { data: zohoEmbedUrl, loading } = useSelector(
    (state: RootState) => state.fetchEmbedZohoUrl
  );

  const getDashboardUrl = useCallback(
    ({ workspace, view }: { workspace: string; view: string }) => {
      dispatch(FetchEmbedZohoActions.request(workspace, view));
    },
    [dispatch]
  );

  const views = useMemo(
    () =>
      ({
        '#projetouno': {
          workspace: '1432280000005304001',
          view: '1432280000241769910',
        },
        '#kpidetransporte': {
          workspace: '1432280000005304001',
          view: '1432280000245985059',
        },
        '#tracking': {
          workspace: '1432280000072387353',
          view: '1432280000134945309',
        },
        '#tracking-inbound-corteva': {
          workspace: '1432280000072387353',
          view: '1432280000134945309',
        },
        '#rework': {
          workspace: '1432280000072387353',
          view: '1432280000082592275',
        },
        '#metrics': {
          workspace: '1432280000072387353',
          view: '1432280000238798146',
        },
        '#inventory': {
          workspace: '1432280000072387353',
          view: '1432280000145024059',
        },
        '#rccp': {
          workspace: '1432280000072387353',
          view: '1432280000188890971',
        },
        '#interdict': {
          workspace: '1432280000072387353',
          view: '1432280000145557780',
        },
        '#ocup-utrisha': {
          workspace: '1432280000072387353',
          view: '1432280000090677672',
        },
        '#temperature-control': {
          workspace: '1432280000005304001',
          view: '1432280000261351022',
        },
        '#multas': {
          workspace: '1432280000072387353',
          view: '1432280000283590737',
        },
      }) as Record<string, { workspace: string; view: string }>,
    []
  );

  useEffect(() => {
    if (['#kpidetransporte'].includes(hash) || !hash) {
      return;
    }
    const painel = views[hash] ?? views['#tracking'];
    getDashboardUrl(painel);
  }, [hash, getDashboardUrl, views]);

  const url = useMemo(() => {
    const zohoUrl = zohoEmbedUrl?.data?.embedUrl;

    const zoho = {
      '#projetouno': {
        show: hasPermissionTo(['DASHBOARD.UNO']),
        url: zohoUrl,
      },
      '#kpidetransporte': {
        show: true,
        url: zohoUrl,
      },
      '#tracking': {
        show: true,
        url: zohoUrl,
      },
      '#tracking-inbound-corteva': {
        show: true,
        url: zohoUrl,
      },
      '#rework': {
        show: hasPermissionTo(['DASHBOARD.REWORK']),
        url: zohoUrl,
      },
      '#inventory': {
        show: hasPermissionTo(['DASHBOARD.INVENTORY']),
        url: zohoUrl,
      },
      '#rccp': {
        show: hasPermissionTo(['DASHBOARD.RCCP']),
        url: zohoUrl,
      },
      '#interdict': {
        show: hasPermissionTo(['DASHBOARD.INTERDICT']),
        url: zohoUrl,
      },
      '#metrics': {
        show: hasPermissionTo(['DASHBOARD.METRICS']),
        url: zohoUrl,
      },
      '#ocup-utrisha': {
        show: true,
        url: zohoUrl,
      },
      '#temperature-control': {
        show: hasPermissionTo(['DASHBOARD.TEMPERATURE_CONTROL']),
        url: zohoUrl,
      },
      '#multas': {
        show: hasPermissionTo(['DASHBOARD.MULTAS']),
        url: zohoUrl,
      },
    }[hash];

    if (zoho?.show) return zoho.url;

    navigate('/dashboard#tracking');
  }, [hash, navigate, zohoEmbedUrl, hasPermissionTo]);

  const isInspectionRoute = pathname.includes('/inspecao-estoque');

  return (
    <Scaffold showComponents={showNavigator}>
      <S.PageContainer ref={ref}>
        {showNavigator && <NavigationDashboard />}
        {showNavigator && isInspectionRoute && <TabbedNavigation />}

        <S.Button onClick={handleClick} isFull={isFullscreen}>
          {isFullscreen ? <S.IconExitFullscreen /> : <S.IconFullscreen />}
        </S.Button>
        {loading ? (
          <Loading />
        ) : (
          <S.Iframe width="100%" height="auto" src={url} loading="lazy" />
        )}
      </S.PageContainer>
    </Scaffold>
  );
};

export { Indicators };
